import { useTranslation } from 'next-i18next';
import { Label, Input, RadioText } from '@components/common/Forms';
import { MixedFormFilterProps } from '@type/filters';


/**
 * @description Generates the fields for the Confine Results filter group.
 * - **recordFunc** - the 'record' function from useBjForm.
 * - **submitFunc** - the function to trigger the form submit. You might want to trigger it on click/blur. Defaults to undefined.
 * - **verticalSpacing** - the spacing between the items. Defaults to 'gap-4'.
 * - **idPrefix** - the prefix for the form elements ids. When using multiple forms on the same page,
 * this is needed to avoid duplicate ids.
 * - **jobBasicInfo** - the basic info for the job (for applicants list); optional.
 */
export const ConfineResultsForm = (props: MixedFormFilterProps) => {
  // Destructure props
  const {
    recordFunc,
    submitFunc,
    verticalSpacing = 'gap-2',
    idPrefix,
    customLabelStyles,
    jobBasicInfo,
    formId,
  } = props;

  // Translation
  const { t } = useTranslation('common');


  // Render component
  // ***********************************************
  return (
    <div className={`grid ${verticalSpacing}`}>
      {/* Only my applicants */}
      <Label htmlFor={`${idPrefix || ''}searchInApplicants`} defaultMargin={false} className={`flex cursor-pointer items-center ${customLabelStyles || ''}`}>
        <Input
          type="checkbox"
          form={formId}
          {...(submitFunc ? { onChange: () => submitFunc() } : {})}
          {...recordFunc('searchInApplicants', {
            id: `${idPrefix || ''}searchInApplicants`,
            defaultValue: 1,
            filtersLabel: 'global.filter.only-my-applicants',
          })}
        />
        <RadioText className="pt-px">{t('global.filter.only-my-applicants')}</RadioText>
      </Label>

      {/* Only saved applicants */}
      <Label htmlFor={`${idPrefix || ''}searchInSavedApplicants`} defaultMargin={false} className={`flex cursor-pointer items-center ${customLabelStyles || ''}`}>
        <Input
          type="checkbox"
          form={formId}
          {...(submitFunc ? { onChange: () => submitFunc() } : {})}
          {...recordFunc('searchInSavedApplicants', {
            id: `${idPrefix || ''}searchInSavedApplicants`,
            defaultValue: 1,
            filtersLabel: 'global.filter.only-saved-applicants',
          })}
        />
        <RadioText className="pt-px">{t('global.filter.only-saved-applicants')}</RadioText>
      </Label>

      {/* With driver's license */}
      <Label htmlFor={`${idPrefix || ''}driversLicence`} defaultMargin={false} className={`flex cursor-pointer items-center ${customLabelStyles || ''}`}>
        <Input
          type="checkbox"
          form={formId}
          {...(submitFunc ? { onChange: () => submitFunc() } : {})}
          {...recordFunc('driversLicence', {
            id: `${idPrefix || ''}driversLicence`,
            defaultValue: 1,
            filtersLabel: 'global.filter.with-drivers-license',
          })}
        />
        <RadioText className="pt-px">{t('global.filter.with-drivers-license')}</RadioText>
      </Label>

      {/* Recommended by agent */}
      <Label htmlFor={`${idPrefix || ''}recommendedByAgent`} defaultMargin={false} className={`flex cursor-pointer items-center ${customLabelStyles || ''}`}>
        <Input
          type="checkbox"
          form={formId}
          {...(submitFunc ? { onChange: () => submitFunc() } : {})}
          {...recordFunc('recommendedByAgent', {
            id: `${idPrefix || ''}recommendedByAgent`,
            defaultValue: 1,
            filtersLabel: 'resume.filter.label.recommendation',
          })}
        />
        <RadioText className="pt-px">{t('resume.filter.label.recommendation')}</RadioText>
      </Label>

      {/* Exclude contacted talents */}
      <Label htmlFor={`${idPrefix || ''}excludeContacted`} defaultMargin={false} className={`flex cursor-pointer items-center ${customLabelStyles || ''}`}>
        <Input
          type="checkbox"
          form={formId}
          {...(submitFunc ? { onChange: () => submitFunc() } : {})}
          {...recordFunc('excludeContacted', {
            id: `${idPrefix || ''}excludeContacted`,
            defaultValue: 1,
            filtersLabel: 'global.filter.exclude-contacted',
          })}
        />
        <RadioText className="pt-px">{t('global.filter.exclude-contacted')}</RadioText>
      </Label>

      {/* With video interview only */}
      <Label htmlFor={`${idPrefix || ''}videoInterviewOnly`} defaultMargin={false} className={`flex cursor-pointer items-center ${customLabelStyles || ''}`}>
        <Input
          type="checkbox"
          form={formId}
          {...(submitFunc ? { onChange: () => submitFunc() } : {})}
          {...recordFunc('videoInterviewOnly', {
            id: `${idPrefix || ''}videoInterviewOnly`,
            defaultValue: 1,
            filtersLabel: 'global.filter.only-video-interview',
          })}
        />
        <RadioText className="pt-px">{t('global.filter.only-video-interview')}</RadioText>
      </Label>

      {/* Passed Through The Interview */
        !!jobBasicInfo && (
          <Label htmlFor={`${idPrefix || ''}passedThroughTheInterview`} defaultMargin={false} className={`flex cursor-pointer items-center has-[:disabled]:!cursor-not-allowed has-[:disabled]:opacity-50 ${customLabelStyles || ''}`}>
            <Input
              type="checkbox"
              form={formId}
              {...(submitFunc ? { onChange: () => submitFunc() } : {})}
              {...recordFunc('passedThroughTheInterview', {
                id: `${idPrefix || ''}passedThroughTheInterview`,
                defaultValue: 1,
                filtersLabel: 'resume.filter.label.with_interview',
              })}
              className="disabled:cursor-not-allowed"
            />
            <RadioText className="pt-px">{t('resume.filter.label.with_interview')}</RadioText>
          </Label>
        )
      }
    </div>
  );
};
